import * as Y from 'yjs'
import { HocuspocusProvider } from '@hocuspocus/provider'
import type { Awareness } from 'y-protocols/awareness'
import { api } from './api'
import type { PartKind, Revision } from './types'
import { lockExpired } from './lock-view'
import { findMatches, applyReplacement, type FindReplaceOptions } from './find-replace'
import { syncYText } from '../components/editor/yt'
import { freshToken } from './session-token'
import { CLIENT_OUTDATED, clientOutdated, isOutdatedRefusal, markClientOutdated, roomParameters } from './client-floor'
import { ROOM_RESTORED, isRoomRestored, noteRoomEpoch } from './room-epoch'
import { makeHistoryEntry, appendHistory } from '../components/editor/history-log'
import { buildExplorerTree, explorerOrder } from './folders'

/**
 * The WIDE find/replace scopes ("this artefact" / "this bid's artefacts") — the part of the
 * feature that opens rooms nobody has open. Everything here works purely off a Y.Doc/Y.Text, on
 * the RAW MARKDOWN string (never a ProseMirror doc: a background room is never mounted into an
 * editor). That is a deliberate split from the local ("this section" / "this part") scope in
 * components/editor/find-in-editor.ts, which searches the live editor's RENDERED text instead —
 * see that module's header for why, and for the resulting (documented, narrow) inconsistency on
 * content containing literal markdown syntax.
 *
 * Every room — the one already open in this tab, or a sibling opened here just for this — goes
 * through the exact same two functions (`applyToRoom`, and `syncYText`/`lockedSectionsOf` beneath
 * it), so "replaced in 4 of 5 parts" cannot mean something different depending on which of those
 * five happened to be open when the member ran it.
 *
 * A SLIDES room (AB#326) holds no markdown: its text lives in Y.Map('slides'), and `applyToRoom`
 * hands it to the deck matcher (slides/find.ts) the open deck's own panel uses, with the deck's lock
 * rules — an element another member has selected (read off the room's awareness) and a slide an AI
 * claims are skipped and reported, never written through.
 */

/** How this module tags its OWN Y.Text writes. Deliberately not RichEditor's `{ src: 'rich-editor'
 *  }` — for the room already open, tagging it differently is what makes RichEditor's own
 *  remote-resync effect treat the write as "not mine" and pull it into the visible WYSIWYG via
 *  `editor.commands.setContent(md, { emitUpdate: false })`, exactly as an AI edit or a peer's edit
 *  would land. */
export const wideReplaceOrigin = { src: 'find-replace-wide' }

/** How long a background room gets to complete its first handshake before this gives up on it.
 *  Generous — unlike the foreground room (lib/room-connection.ts) nobody is watching a chip for
 *  this one; failing fast only costs reporting the part as skipped rather than replaced. */
export const CONNECT_TIMEOUT_MS = 10_000

/** One part this operation may touch. */
export interface PartTarget {
  /** The room key ("bid/artefact/part") — what a background connection opens. */
  room: string
  /** "<artefact title> — <part title>", for the results list. */
  title: string
  /** The part's kind, when known: a slides room is searched as a deck (else: read off the room). */
  kind?: PartKind
  /** Its artefact's id and title: the results are summed per artefact (AB#366). */
  artefact?: string
  artefactTitle?: string
}

export type PartOutcome =
  /** `skipped`: matches left alone because another member or an AI holds them (a deck). */
  | { room: string; title: string; status: 'replaced'; count: number; skipped?: number }
  | { room: string; title: string; status: 'matched'; count: number } // find-only preview
  | { room: string; title: string; status: 'unchanged' }
  | { room: string; title: string; status: 'locked' }
  | { room: string; title: string; status: 'failed'; message: string }

/**
 * Every text part reachable from `room`'s scope: its own artefact, or the whole bid — prose and
 * slides parts alike. Grid parts are left out — they keep their Y.Text empty by design (their
 * content lives in Y.Map('rows')), so a markdown search has nothing to find there.
 *
 * In order (AB#366, continuous Find): the artefacts as the explorer lists them (folders included —
 * an unreadable folder list degrades to the artefacts' own order), each one's parts in its order.
 */
export async function collectTargets(room: string, scope: 'artefact' | 'bid'): Promise<PartTarget[]> {
  const [bidId, artefactId] = room.split('/')
  if (!bidId) return []
  const [{ artefacts }, folders] = await Promise.all([
    api.artefacts(bidId),
    scope === 'bid' ? Promise.resolve().then(() => api.folders(bidId)).then((r) => r.folders, () => []) : Promise.resolve([]),
  ])
  const docs = scope === 'bid' ? explorerOrder(buildExplorerTree(folders, artefacts)) : artefacts.filter((a) => a.artefactId === artefactId)
  return docs.flatMap((doc) =>
    doc.parts
      .filter((p) => p.kind !== 'grid')
      .map((p) => ({ room: p.name, title: `${doc.title} — ${p.title}`, ...(p.kind ? { kind: p.kind } : {}), artefact: doc.artefactId, artefactTitle: doc.title })),
  )
}

/** The section numbers currently held by ANYONE in this room, '' meaning the whole part — the
 *  same shape Canvas feeds to the editor's lock enforcement, read straight off the room's own
 *  Y.Map('revisions') rather than threaded in, since a background room has no Canvas computing it. */
function lockedSectionsOf(revisions: Y.Map<unknown>): string[] {
  const now = Date.now()
  return ([...revisions.values()] as Revision[])
    .filter((r) => r.status === 'working' && !lockExpired(r, now))
    .map((r) => r.section ?? '')
}

/**
 * Read a room's content, search it, and — unless this is a find-only preview — write the
 * replacement back and log it. Shared by the room already open and every background one, so the
 * one policy (`syncYText`'s lock guard) is the only place that can ever refuse a write.
 *
 * `replacement === null` means "count only" — the find (no replace) preview for a wide scope: no
 * lock check is needed because nothing is written, so a locked part still reports its match count.
 */
async function applyToRoom(
  ydoc: Y.Doc,
  ytext: Y.Text,
  offset: number,
  target: PartTarget,
  query: string,
  replacement: string | null,
  options: FindReplaceOptions,
  actorName: string,
  historyPrompt: string,
  awareness: Awareness | null,
): Promise<PartOutcome> {
  // A deck's text lives in Y.Map('slides'), never in this Y.Text (slides/model.ts's discriminator).
  if (target.kind === 'slides' || ydoc.getMap('meta').get('kind') === 'slides') return applyToDeck(ydoc, awareness, target, query, replacement, options, actorName, historyPrompt)
  const before = ytext.toString()
  const matches = findMatches(before, query, options)
  if (matches.length === 0) return { room: target.room, title: target.title, status: 'unchanged' }
  if (replacement === null) return { room: target.room, title: target.title, status: 'matched', count: matches.length }

  const locked = lockedSectionsOf(ydoc.getMap('revisions'))
  const after = applyReplacement(before, matches, replacement)
  const outcome = syncYText(ytext, after, wideReplaceOrigin, locked, offset)
  if (outcome === 'blocked') return { room: target.room, title: target.title, status: 'locked' }

  // outcome is 'applied' here (matches.length > 0 already ruled out 'unchanged').
  const entry = makeHistoryEntry({ actor: actorName, kind: 'human', before, after: ytext.toString(), prompt: historyPrompt })
  if (entry) appendHistory(ydoc, entry)
  return { room: target.room, title: target.title, status: 'replaced', count: matches.length }
}

/**
 * `applyToRoom` for a slides room: the deck matcher with the deck's locks (slides/find.ts, loaded
 * with the first deck — the deck's code is its own chunk), ONE history entry in the shape the
 * editor's own batches have, with the panel's prompt. A deck whose every match is held reports
 * `locked`; one partly held, `replaced` with the `skipped` count.
 */
async function applyToDeck(
  ydoc: Y.Doc,
  awareness: Awareness | null,
  target: PartTarget,
  query: string,
  replacement: string | null,
  options: FindReplaceOptions,
  actorName: string,
  historyPrompt: string,
): Promise<PartOutcome> {
  const { replaceInDeckRoom } = await import('../slides/find')
  const o = replaceInDeckRoom(ydoc, awareness, query, replacement, options, actorName, historyPrompt, wideReplaceOrigin)
  const { room, title } = target
  if (o.status === 'replaced') return { room, title, status: 'replaced', count: o.count, ...(o.skipped ? { skipped: o.skipped } : {}) }
  return { room, title, ...o }
}

/** Resolve once the room's first handshake lands, or `false` after `timeoutMs` of nothing. */
function waitSynced(provider: HocuspocusProvider, timeoutMs: number): Promise<boolean> {
  if (provider.synced) return Promise.resolve(true)
  return new Promise((resolve) => {
    let timer: ReturnType<typeof setTimeout>
    const onSynced = (): void => {
      clearTimeout(timer)
      provider.off('synced', onSynced)
      resolve(true)
    }
    timer = setTimeout(() => {
      provider.off('synced', onSynced)
      resolve(false)
    }, timeoutMs)
    provider.on('synced', onSynced)
  })
}

/** Open `target.room` just long enough to read/replace its content, then close it. Nobody is
 *  watching this connection the way Canvas's chip watches the open room, so failure degrades to a
 *  reported outcome rather than a retry/health loop. */
async function applyToBackgroundRoom(
  target: PartTarget,
  query: string,
  replacement: string | null,
  options: FindReplaceOptions,
  wsUrl: string,
  actorName: string,
  historyPrompt: string,
): Promise<PartOutcome> {
  const r = await withBackgroundRoom(target.room, wsUrl, async (ydoc, awareness, restored) => {
    const { offset } = await api.sections(target.room).catch(() => ({ offset: 0 }))
    if (restored()) return ROOM_RESTORED
    return applyToRoom(ydoc, ydoc.getText('content'), offset, target, query, replacement, options, actorName, historyPrompt, awareness)
  })
  return r.ok ? r.value : { room: target.room, title: target.title, status: 'failed', message: r.message }
}

/** How many times a background room refused with `room_restored` is reopened fresh. */
const RESTORED_RETRIES = 1

/**
 * Open `room` in the background, hand its synced Y.Doc to `use`, then close it — the connection the
 * wide find/replace scopes and the artefact's anchor list (AB#350, lib/artefact-anchors.ts) share.
 * `use` may return ROOM_RESTORED (it saw `restored()` turn true) to have the room reopened fresh.
 * A room replaced server-side while it is open here (`room_restored`, lib/room-epoch.ts) is reopened
 * on a NEW Y.Doc, once — the replacement is read, never the content it replaced.
 */
export async function withBackgroundRoom<T>(
  room: string,
  wsUrl: string,
  use: (ydoc: Y.Doc, awareness: Awareness, restored: () => boolean) => Promise<T | typeof ROOM_RESTORED>,
): Promise<{ ok: true; value: T } | { ok: false; message: string }> {
  // Below the backend's version floor no room is opened on this bundle's provider (lib/client-floor.ts).
  if (clientOutdated()) return { ok: false, message: CLIENT_OUTDATED }
  for (let attempt = 0; attempt <= RESTORED_RETRIES; attempt++) {
    const outcome = await openAndUse(room, wsUrl, use)
    if (outcome !== ROOM_RESTORED) return outcome
  }
  return { ok: false, message: ROOM_RESTORED }
}

/** One connection of {@link withBackgroundRoom}: its outcome, or ROOM_RESTORED to reopen fresh. */
async function openAndUse<T>(
  room: string,
  wsUrl: string,
  use: (ydoc: Y.Doc, awareness: Awareness, restored: () => boolean) => Promise<T | typeof ROOM_RESTORED>,
): Promise<{ ok: true; value: T } | { ok: false; message: string } | typeof ROOM_RESTORED> {
  const ydoc = new Y.Doc()
  let onRestored = (): void => {}
  const restored = new Promise<typeof ROOM_RESTORED>((resolve) => {
    onRestored = () => resolve(ROOM_RESTORED)
  })
  let wasRestored = false
  const markRestored = (reason: string | undefined): void => {
    if (!isRoomRestored(reason)) return
    wasRestored = true
    onRestored()
  }
  // The room epoch, as in Canvas: announced by the server, presented back on a reconnect.
  const parameters: Record<string, unknown> = roomParameters()
  // Same live-token rule as the open room (Canvas): a function, re-evaluated per connection, so a
  // bid-wide replace started after a long idle is not refused part by part. Same `spa` parameter too.
  const provider = new HocuspocusProvider({
    url: wsUrl,
    name: room,
    token: () => freshToken(),
    document: ydoc,
    parameters,
    onAuthenticationFailed: ({ reason }) => {
      if (isOutdatedRefusal(reason)) markClientOutdated('spa')
      markRestored(reason)
    },
    onClose: ({ event }) => markRestored(event?.reason),
    onStateless: ({ payload }) => noteRoomEpoch(parameters, payload),
  })
  try {
    const synced = await Promise.race([waitSynced(provider, CONNECT_TIMEOUT_MS), restored])
    if (synced === ROOM_RESTORED) return ROOM_RESTORED
    if (!synced) return { ok: false, message: 'timed out' }
    const value = await use(ydoc, provider.awareness!, () => wasRestored)
    return value === ROOM_RESTORED ? ROOM_RESTORED : { ok: true, value }
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) }
  } finally {
    provider.destroy()
    ydoc.destroy()
  }
}

/** The room already open in this tab — reuses its live connection instead of opening a second one
 *  to the same room. */
export interface OpenRoom {
  room: string
  ydoc: Y.Doc
  ytext: Y.Text
  /** Its awareness: who holds which element of a deck. */
  awareness?: Awareness | null
}

export interface WideReplaceArgs {
  targets: PartTarget[]
  query: string
  /** null = preview only (count matches, write nothing, log nothing). */
  replacement: string | null
  options: FindReplaceOptions
  wsUrl: string
  actorName: string
  /** The history entry's `prompt`, already localised and interpolated by the caller — this
   *  module has no `t()` to build it with. */
  historyPrompt: string
  open?: OpenRoom
  onProgress?: (done: number, total: number) => void
}

/**
 * Run the search (and, unless previewing, the replace) over every target, one room at a time.
 * Sequential on purpose: a bid's artefacts can run into dozens of parts, and opening that many
 * Hocuspocus connections at once would be a self-inflicted load spike for a background operation
 * nobody is blocked on.
 */
export async function applyWideReplace(args: WideReplaceArgs): Promise<PartOutcome[]> {
  const { targets, query, replacement, options, wsUrl, actorName, historyPrompt, open, onProgress } = args
  const results: PartOutcome[] = []
  for (let i = 0; i < targets.length; i++) {
    const target = targets[i]!
    const result =
      open && target.room === open.room
        ? await applyToRoom(open.ydoc, open.ytext, (await api.sections(target.room).catch(() => ({ offset: 0 }))).offset, target, query, replacement, options, actorName, historyPrompt, open.awareness ?? null)
        : await applyToBackgroundRoom(target, query, replacement, options, wsUrl, actorName, historyPrompt)
    results.push(result)
    onProgress?.(i + 1, targets.length)
  }
  return results
}
